/* =============================================================================
   assets/predictive-search.js: vanilla port of the search drawer
     - app/components/SearchFormPredictive.tsx   (form, fetchResults, goToSearch)
     - app/components/SearchResultsPredictive.tsx (results, Empty, closeSearch)
     - PageLayout.tsx SearchAside() render prop    (Loading / suggestions / results
                                                   / "View all results")
   The Hydrogen loader's predictiveSearch (limit 5, limitScope EACH, every type)
   becomes GET /search/suggest.json with the same types and limits.
   Every class string below is copied verbatim from those components; keep
   them complete (Tailwind scans this file).
   ========================================================================== */
(() => {
  'use strict';

  const overlay = document.querySelector('.overlay[data-aside="search"]');
  if (!overlay) return;
  const root = overlay.querySelector('.predictive-search');
  const form = root ? root.querySelector('form.predictive-search-form') : null;
  const input = form ? form.querySelector('input[type="search"]') : null;
  const goButton = form ? form.querySelector('button') : null;
  if (!root || !form || !input) return;

  const SEARCH_ENDPOINT = '/search';
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const moneyFormat = new Intl.NumberFormat('en-US', {style: 'currency', currency});
  const datalistId = input.getAttribute('list') || '';

  // PredictiveSuggestions(): server-rendered by snippets/aside-search.liquid.
  const suggestionsHTML = Array.from(root.children)
    .filter((child) => child !== form)
    .map((child) => child.outerHTML)
    .join('');

  const emptyResult = () => ({
    total: 0,
    items: {articles: [], collections: [], products: [], pages: [], queries: []},
  });

  let state = 'idle';
  let term = '';
  let result = emptyResult();
  let controller = null;
  let sequence = 0;

  const aside = () => window.UndepletedAside || {close() {}};

  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const money = (amount) => escapeHtml(moneyFormat.format(Number(amount)));

  /* lib/search.ts urlWithTrackingParams() */
  function urlWithTrackingParams(baseUrl, suggestUrl, searchTerm) {
    let search = new URLSearchParams({q: encodeURIComponent(searchTerm || '')}).toString();
    const at = (suggestUrl || '').indexOf('?');
    const trackingParams = at > -1 ? suggestUrl.slice(at + 1) : '';
    if (trackingParams) search = `${search}&${trackingParams}`;
    return `${baseUrl}?${search}`;
  }

  /* Hydrogen <Image src width height>: Shopify CDN crop + 1x/2x/3x srcset */
  function sized(src, width, height) {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(width));
      url.searchParams.set('height', String(height));
      url.searchParams.set('crop', 'center');
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  function image(img, size) {
    if (!img || !img.url) return '';
    const one = sized(img.url, size, size);
    const two = sized(img.url, size * 2, size * 2);
    const three = sized(img.url, size * 3, size * 3);
    return `<img alt="${escapeHtml(img.alt || '')}" decoding="async" height="${size}" loading="lazy" src="${escapeHtml(one)}" srcset="${escapeHtml(`${one} 1x, ${two} 2x, ${three} 3x`)}" width="${size}" style="aspect-ratio: ${size} / ${size};">`;
  }

  const pathOf = (url) => String(url || '').split('?')[0];

  function featured(item) {
    if (item && item.featured_image && item.featured_image.url) {
      return {url: item.featured_image.url, alt: item.featured_image.alt};
    }
    if (item && typeof item.image === 'string' && item.image) return {url: item.image, alt: ''};
    return null;
  }

  function productsMarkup(products) {
    if (!products.length) return '';
    const rows = products
      .map((product) => {
        const url = urlWithTrackingParams(`/products/${product.handle}`, product.url, term);
        const price = product.price;
        return `<li class="predictive-search-result-item"><a href="${escapeHtml(url)}">${image(featured(product), 50)}<div><p>${escapeHtml(product.title)}</p><small>${price ? `<div>${money(price)}</div>` : ''}</small></div></a></li>`;
      })
      .join('');
    return `<div class="predictive-search-result"><h5>Products</h5><ul>${rows}</ul></div>`;
  }

  function collectionsMarkup(collections) {
    if (!collections.length) return '';
    const rows = collections
      .map((collection) => {
        const url = urlWithTrackingParams(`/collections/${collection.handle}`, collection.url, term);
        return `<li class="predictive-search-result-item"><a href="${escapeHtml(url)}">${image(featured(collection), 50)}<div><span>${escapeHtml(collection.title)}</span></div></a></li>`;
      })
      .join('');
    return `<div class="predictive-search-result"><h5>Collections</h5><ul>${rows}</ul></div>`;
  }

  function pagesMarkup(pages) {
    if (!pages.length) return '';
    const rows = pages
      .map((page) => {
        const url = urlWithTrackingParams(`/pages/${page.handle}`, page.url, term);
        return `<li class="predictive-search-result-item"><a href="${escapeHtml(url)}"><div><span>${escapeHtml(page.title)}</span></div></a></li>`;
      })
      .join('');
    return `<div class="predictive-search-result"><h5>Pages</h5><ul>${rows}</ul></div>`;
  }

  function articlesMarkup(articles) {
    if (!articles.length) return '';
    const rows = articles
      .map((article) => {
        const url = urlWithTrackingParams(pathOf(article.url), article.url, term || '');
        return `<li class="predictive-search-result-item"><a href="${escapeHtml(url)}">${image(featured(article), 50)}<div><span>${escapeHtml(article.title)}</span></div></a></li>`;
      })
      .join('');
    return `<div class="predictive-search-result"><h5>Articles</h5><ul>${rows}</ul></div>`;
  }

  function queriesMarkup(queries) {
    if (!queries.length) return '';
    const options = queries.map((suggestion) => (suggestion ? `<option value="${escapeHtml(suggestion.text)}"></option>` : '')).join('');
    return `<datalist id="${escapeHtml(datalistId)}">${options}</datalist>`;
  }

  function markup() {
    const {items, total} = result;
    if (state === 'loading' && term) {
      return '<div class="text-2xs uppercase text-ink-3 pt-6" role="status">Loading</div>';
    }
    if (!total) {
      // The no-term state and the no-results state are different states.
      return term ? `<p>No results found for <q>${escapeHtml(term)}</q></p>` : suggestionsHTML;
    }
    const viewAll =
      term && total
        ? `<div class="pt-6 [--link-hover:var(--color-ink)] [--link:var(--color-ink)]"><a class="inline-block border-b border-rule-control pb-1.5 text-xs uppercase" href="${escapeHtml(`${SEARCH_ENDPOINT}?q=${term}`)}">View all results →</a></div>`
        : '';
    return (
      queriesMarkup(items.queries) +
      productsMarkup(items.products) +
      collectionsMarkup(items.collections) +
      pagesMarkup(items.pages) +
      articlesMarkup(items.articles) +
      viewAll
    );
  }

  function render() {
    while (form.nextSibling) form.nextSibling.remove();
    form.insertAdjacentHTML('afterend', markup());
  }

  function normalize(json) {
    const results = (json && json.resources && json.resources.results) || {};
    const items = {
      articles: results.articles || [],
      collections: results.collections || [],
      pages: results.pages || [],
      products: results.products || [],
      queries: results.queries || [],
    };
    const total = Object.values(items).reduce((sum, list) => sum + list.length, 0);
    return {total, items};
  }

  /* fetchResults(): fetcher.submit({q, limit: 5, predictive: true}, GET /search) */
  function fetchResults() {
    const value = input.value || '';
    const id = ++sequence;
    term = String(value);
    state = 'loading';
    render();
    if (controller) controller.abort();
    controller = new AbortController();
    const q = value.trim();
    const request = q
      ? fetch(
          `${shopRoot}search/suggest.json?q=${encodeURIComponent(q)}&resources%5Btype%5D=product,collection,page,article,query&resources%5Blimit%5D=5&resources%5Blimit_scope%5D=each`,
          {signal: controller.signal, headers: {Accept: 'application/json'}},
        )
          .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`suggest ${response.status}`))))
          .then(normalize)
      : Promise.resolve(emptyResult());
    request.then(
      (next) => {
        if (id !== sequence) return;
        result = next;
        state = 'idle';
        render();
      },
      (error) => {
        if (id !== sequence || (error && error.name === 'AbortError')) return;
        result = emptyResult();
        state = 'idle';
        render();
      },
    );
  }

  /* SearchResultsPredictive closeSearch(): blur + clear the input, close the drawer */
  function closeSearch() {
    input.blur();
    input.value = '';
    aside().close();
  }

  input.addEventListener('input', fetchResults);
  input.addEventListener('focus', fetchResults);

  /* <fetcher.Form> gets no action prop, so React Router's useFormAction()
     resolves it to the root route ("/") plus the router location's search,
     verbatim (e.g. "/?q=serum" on /search?q=serum). The router location is the
     URL the document loaded with, and it moves only on router navigations: on
     the PDP a pick (navigate) moves it, Hydrogen's own replaceState of
     ?Size=Single on load does not. So: start from the navigation entry's URL,
     follow popstate and the PDP's picks ('undepleted:locationchange'). The form
     never submits natively (resetInput); this only keeps the attribute live's. */
  function searchAtLoad() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.name) return new URL(nav.name).search;
    } catch (error) {
      /* fall through */
    }
    return window.location.search;
  }

  function syncAction(from) {
    let search = typeof from === 'string' ? from : window.location.search;
    const params = new URLSearchParams(search);
    const indexValues = params.getAll('index');
    if (indexValues.some((v) => v === '')) {
      params.delete('index');
      indexValues.filter((v) => v).forEach((v) => params.append('index', v));
      const qs = params.toString();
      search = qs ? `?${qs}` : '';
    }
    const action = `/${search}`;
    if (form.getAttribute('action') !== action) form.setAttribute('action', action);
  }
  syncAction(searchAtLoad());
  window.addEventListener('popstate', () => syncAction());
  window.addEventListener('undepleted:locationchange', () => syncAction());

  // resetInput(): the form never submits; Enter only blurs a non-empty field.
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (input.value) input.blur();
  });

  // goToSearch(): navigate to /search[?q=term], close the drawer
  if (goButton) {
    goButton.addEventListener('click', () => {
      const value = input.value;
      aside().close();
      window.location.assign(SEARCH_ENDPOINT + (value ? `?q=${value}` : ''));
    });
  }

  // every result / suggestion / "View all results" link: onClick={closeSearch}
  root.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (link && root.contains(link) && !form.contains(link)) closeSearch();
  });
})();
